import { getEmployeesWhoPerformServices } from '@/helpers/placeHelper';
import { Place, PlaceEmployee } from '@/types/state/shared';
import { ANY_EMPLOYEE_ID } from '../constants';
import type { SelectEmployeeListItem } from './SelectEmployeeList.types';

/**
 * One employee mapped for the list. Scores live on the place's reviews, not on the
 * employee (as on PlacePage).
 */
export const toEmployeeListItem = (employee: PlaceEmployee, place: Place): SelectEmployeeListItem => {
  const employeeStats = place.reviews?.employeeStats;
  return {
    id: employee.id,
    name: employee.about.name,
    jobTitle: employee.about.jobTitle ?? undefined,
    avatarUrl: employee.about.settings?.showPhoto !== false ? employee.about.avatar : undefined,
    rating: employeeStats?.[employee.id]?.score ? { score: employeeStats[employee.id].score as number } : undefined,
    // Same field the place page's employee box shows through ReadMore; often absent.
    info: employee.about.info ?? undefined,
  };
};

/**
 * Returns the raw `PlaceEmployee[]` too — pick handlers look up `about.externalId` on it
 * when serialising `sr`.
 */
export const getBookableEmployeeListItems = (
  place: Place,
  serviceIds: number[],
): { bookableEmployees: PlaceEmployee[]; items: SelectEmployeeListItem[] } => {
  const bookableEmployees: PlaceEmployee[] = getEmployeesWhoPerformServices(place.employees ?? [], serviceIds);
  return { bookableEmployees, items: bookableEmployees.map((employee) => toEmployeeListItem(employee, place)) };
};

/**
 * Whether the employee view holds an answer other than the current one. More than one
 * bookable employee is the ordinary case; the second clause covers a pick the service
 * filter excludes — a deep link or a legacy `sr` can name someone who does not perform the
 * whole selection, and withholding the affordance there leaves the visitor filtered by an
 * employee they cannot switch away from. "Valfri utövare" is not checked against the list
 * because the view renders that row itself.
 */
export const canOfferEmployeeChange = (items: SelectEmployeeListItem[], selectedEmployeeId?: number): boolean =>
  items.length > 1 ||
  (selectedEmployeeId != null &&
    selectedEmployeeId !== ANY_EMPLOYEE_ID &&
    !items.some((item) => item.id === selectedEmployeeId));
